import { useState, useEffect, useRef, useCallback } from "react";
import { AppState, type AppStateStatus } from "react-native";
import { getJpegStreamUrl, extractCameraIdForLegacyApi, LegacyStreamError } from "@src/services/api/legacy-stream-api";
import { api } from "@src/services/api/client";
import { ApiError } from "@src/services/api/request";
import { isLegacyAppCameraKey } from "@src/utils/key-parser";

const FRAME_REFRESH_INTERVAL_MS = 500;
const URL_UPDATE_INTERVAL_MS = 20_000;
const START_TIMEOUT_MS = 90_000;
const STREAM_TIMEOUT_MS = 5 * 60_000;

export type JpegStreamError =
  | "network"
  | "unauthorized_user"
  | "unauthorized_camera"
  | "timeout_start"
  | "timeout_stream"
  | "unknown";

interface UseJpegStreamOptions {
  cameraKey: string;
  tenantId: string;
  siteId: string;
}

interface UseJpegStreamReturn {
  currentFrame: string | null;
  isLoading: boolean;
  isPlaying: boolean;
  error: JpegStreamError | null;
  errorMessage: string | null;
  startStream: () => void;
  stopStream: () => void;
  restartStream: () => void;
  timeRemaining: number | null;
}

const ERROR_MESSAGES: Record<JpegStreamError, string> = {
  network: "Unable to stream live feed, check your network connection",
  unauthorized_user: "Your session has expired. Please log in again.",
  unauthorized_camera: "Unable to stream live feed, you don't have access to this camera",
  timeout_start: "Unable to start stream. Please try again.",
  timeout_stream: "Stream session ended. You can restart the stream.",
  unknown: "An error occurred while streaming. Please try again.",
};

export function useJpegStream({ cameraKey, tenantId, siteId }: UseJpegStreamOptions): UseJpegStreamReturn {
  const [currentFrame, setCurrentFrame] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState<JpegStreamError | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);

  const streamUrlRef = useRef<string | null>(null);
  const lastUrlFetchRef = useRef<number>(0);
  const frameIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const streamTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const streamStartTimeRef = useRef<number>(0);
  const isPlayingRef = useRef(false);
  const hasReceivedFirstFrame = useRef(false);

  const clearAllTimers = useCallback(() => {
    if (frameIntervalRef.current) {
      clearInterval(frameIntervalRef.current);
      frameIntervalRef.current = null;
    }
    if (startTimeoutRef.current) {
      clearTimeout(startTimeoutRef.current);
      startTimeoutRef.current = null;
    }
    if (streamTimeoutRef.current) {
      clearTimeout(streamTimeoutRef.current);
      streamTimeoutRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  const handleError = useCallback(
    (errorType: JpegStreamError, message?: string) => {
      setError(errorType);
      setIsPlaying(false);
      isPlayingRef.current = false;
      clearAllTimers();
      setErrorMessage(message || ERROR_MESSAGES[errorType]);
    },
    [clearAllTimers],
  );

  const fetchStreamUrl = useCallback(async (): Promise<string | null> => {
    try {
      let url: string | null;

      if (isLegacyAppCameraKey(cameraKey)) {
        const cameraId = extractCameraIdForLegacyApi(cameraKey);
        url = await getJpegStreamUrl(cameraId);
      } else {
        // TODO: This endpoint doesn't seem to exist?
        url = await api.cameras.getJpegStreamUrl(tenantId, siteId, cameraKey);
      }

      if (url) {
        streamUrlRef.current = url;
        lastUrlFetchRef.current = Date.now();
      }
      return url;
    } catch (err) {
      if (err instanceof LegacyStreamError || err instanceof ApiError) {
        const status = err instanceof LegacyStreamError ? err.status : err.status;
        switch (status) {
          case 401:
            handleError("unauthorized_user");
            break;
          case 403:
            handleError("unauthorized_camera");
            break;
          case 404:
            return null;
          default:
            handleError("network");
        }
      } else {
        handleError("network");
      }
      return null;
    }
  }, [cameraKey, tenantId, siteId, handleError]);

  const refreshFrame = useCallback(async () => {
    if (!isPlayingRef.current) return;

    const timeSinceUrlFetch = Date.now() - lastUrlFetchRef.current;
    if (!streamUrlRef.current || timeSinceUrlFetch >= URL_UPDATE_INTERVAL_MS) {
      await fetchStreamUrl();
    }

    if (streamUrlRef.current && isPlayingRef.current) {
      const frameUrl = `${streamUrlRef.current}${streamUrlRef.current.includes("?") ? "&" : "?"}_t=${Date.now()}`;

      try {
        const response = await fetch(frameUrl, { method: "GET" });

        if (response.status === 404) {
          return;
        }

        if (response.ok) {
          const blob = await response.blob();

          // Convert blob to base64 data URL for expo-image compatibility with presigned S3 URLs
          const reader = new FileReader();
          reader.onloadend = () => {
            if (isPlayingRef.current && typeof reader.result === "string") {
              setCurrentFrame(reader.result);
            }
          };
          reader.readAsDataURL(blob);
        }
      } catch {
        // Silently ignore fetch errors for individual frames - stream will recover
      }

      if (!hasReceivedFirstFrame.current) {
        hasReceivedFirstFrame.current = true;
        setIsLoading(false);

        if (startTimeoutRef.current) {
          clearTimeout(startTimeoutRef.current);
          startTimeoutRef.current = null;
        }

        streamStartTimeRef.current = Date.now();
        streamTimeoutRef.current = setTimeout(() => {
          handleError("timeout_stream");
        }, STREAM_TIMEOUT_MS);

        countdownIntervalRef.current = setInterval(() => {
          const elapsed = Date.now() - streamStartTimeRef.current;
          const remaining = Math.max(0, Math.ceil((STREAM_TIMEOUT_MS - elapsed) / 1000));
          setTimeRemaining(remaining);
        }, 1000);
      }
    }
  }, [fetchStreamUrl, handleError]);

  const startStream = useCallback(() => {
    if (isPlayingRef.current) return;

    setIsPlaying(true);
    setIsLoading(true);
    setError(null);
    setErrorMessage(null);
    setCurrentFrame(null);
    setTimeRemaining(null);

    isPlayingRef.current = true;
    hasReceivedFirstFrame.current = false;
    streamUrlRef.current = null;
    lastUrlFetchRef.current = 0;

    startTimeoutRef.current = setTimeout(() => {
      if (!hasReceivedFirstFrame.current) {
        handleError("timeout_start");
      }
    }, START_TIMEOUT_MS);

    refreshFrame();
    frameIntervalRef.current = setInterval(refreshFrame, FRAME_REFRESH_INTERVAL_MS);
  }, [refreshFrame, handleError]);

  const stopStream = useCallback(() => {
    setIsPlaying(false);
    isPlayingRef.current = false;
    clearAllTimers();
    setTimeRemaining(null);
  }, [clearAllTimers]);

  const restartStream = useCallback(() => {
    stopStream();
    setTimeout(() => {
      startStream();
    }, 100);
  }, [stopStream, startStream]);

  useEffect(() => {
    const handleAppStateChange = (nextAppState: AppStateStatus) => {
      if (nextAppState !== "active" && isPlayingRef.current) {
        stopStream();
      }
    };

    const subscription = AppState.addEventListener("change", handleAppStateChange);
    return () => {
      subscription.remove();
    };
  }, [stopStream]);

  useEffect(() => {
    return () => {
      isPlayingRef.current = false;
      clearAllTimers();
    };
  }, [clearAllTimers]);

  return {
    currentFrame,
    isLoading,
    isPlaying,
    error,
    errorMessage,
    startStream,
    stopStream,
    restartStream,
    timeRemaining,
  };
}
